import { useMemo } from "react";
import { Box, Theme, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { isMobile } from "../../utils";
import FiltersMobile from "../../components/TableFilters/FiltersMobile";
import { useFilterState } from "../../state/filters";
import {
  filtersDesktopInputs,
  filtersMobileInputs,
  sortFields,
  tableHeaders
} from "./utils";
import FiltersDesktop from "../../components/TableFilters/FiltersDesktop";
import useChargesHistory from "../../hooks/useChargesHistory";
import Loading from "../../components/Loading/Loading";
import TableComponent from "../../components/Table/Table";
import Row from "./Row";
import HelpButton from "../../components/payments/HelpContent/HelpButton";
import { useCompanyPageTitle } from "../../state/selectedPaymentConfiguration";
import { PAYMENTS_HELP_CONTENT } from "../../constants";
import { semanticColors } from "../../utils/colors";
import { typography } from "../../utils/typography";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingTop: "48px",
    paddingBottom: "48px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "24px",
    [theme.breakpoints.up("sm")]: {
      paddingLeft: "24px",
      paddingRight: "24px"
    }
  },
  title: {
    ...typography.heading.xl,
    color: semanticColors.text.primary,
    textAlign: "center" as const
  },
  noResults: {
    color: semanticColors.text.primary,
    marginTop: theme.spacing(4)
  }
}));

export default function ChargesHistory() {
  const classes = useStyles();
  const { t } = useTranslation();
  const companyTitle = useCompanyPageTitle();
  // Filter and table labels are translated, so rebuild them when the language changes.
  const mobileFilters = useMemo(() => filtersMobileInputs(t), [t]);
  const desktopFilters = useMemo(() => filtersDesktopInputs(t), [t]);
  const sortOptions = useMemo(() => sortFields(t), [t]);
  const headers = useMemo(() => tableHeaders(t), [t]);
  const {
    data,
    onChangeFilters,
    onChangeSort,
    onChangeTypeState,
    onChangeResetFiltersState,
    haveActiveFilters
  } = useFilterState("cobrancas");
  const { filteredItems, total, loading, error } = useChargesHistory({
    page: data.page,
    rowsPerPage: data.rowsPerPage,
    filters: data.filters,
    sort: data.sort
  });

  return (
    <Box className={classes.container}>
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Typography className={classes.title}>
          {companyTitle(t("chargesHistory.title"))}
        </Typography>
        <HelpButton linkName={PAYMENTS_HELP_CONTENT.LIST} />
      </Box>
      {isMobile ? (
        <FiltersMobile
          state={data}
          filters={mobileFilters}
          mobileExpanded={data.mobileExpanded}
          sortFields={sortOptions}
          onChangeFilters={onChangeFilters}
          onChangeSort={onChangeSort}
          onChangeTypeState={onChangeTypeState}
          spaceItems={2}
          onChangeReset={haveActiveFilters ? onChangeResetFiltersState : null}
        />
      ) : (
        <FiltersDesktop
          state={data}
          filters={desktopFilters}
          sortFields={sortOptions}
          onChangeFilters={onChangeFilters}
          onChangeSort={onChangeSort}
          spaceItems={3}
          resultsLength={filteredItems.length || undefined}
          onChangeReset={haveActiveFilters ? onChangeResetFiltersState : null}
        />
      )}
      {loading ? (
        <Box alignItems="center" justifyContent="center" display="flex" mt={10}>
          <Loading />
        </Box>
      ) : error ? (
        // Without this branch a failed request looks exactly like an empty
        // history, which reads as "you were never paid".
        <Typography
          className={classes.noResults}
          sx={{ color: semanticColors.status.error }}
        >
          {t("common.loadError")}
        </Typography>
      ) : filteredItems.length > 0 ? (
        <TableComponent
          headerCfg={headers}
          total={total}
          page={data.page}
          rowsPerPage={data.rowsPerPage}
          onChangePagination={onChangeTypeState}
        >
          {filteredItems.map((row: any) => (
            <Row key={row.id} row={row} />
          ))}
        </TableComponent>
      ) : (
        <Typography className={classes.noResults}>
          {t("common.noResults")}
        </Typography>
      )}
    </Box>
  );
}
